import React, { useMemo, useState, useEffect, useRef } from "react";
import Select from "react-select";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const ComparisonGraph = ({ reviews, chartId = "avgRatingChart" }) => {
  const chartRef = useRef(null);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const COLORS = [
    "#3366CC", // Blue
    "#DC3912", // Red
    "#FF9900", // Orange
    "#109618", // Green
    "#990099", // Purple
    "#0099C6", // Cyan
    "#DD4477", // Pink
    "#66AA00", // Lime
    "#B82E2E", // Dark Red
    "#316395", // Navy
    "#994499", // Violet
    "#22AA99", // Teal
    "#AAAA11", // Mustard
    "#6633CC", // Indigo
    "#E67300", // Dark Orange
  ];

  // Extract unique product codes
  const productCodes = useMemo(() => {
    const codes = reviews
      .map((r) => (r[6] ? String(r[6]).trim() : null))
      .filter(Boolean);
    return Array.from(new Set(codes));
  }, [reviews]);

  // Assign persistent random colors per product
  const productColors = useMemo(() => {
    const colors = {};
    productCodes.forEach((code, idx) => {
      colors[code] = COLORS[idx % COLORS.length]; // cycle if more products than colors
    });
    return colors;
  }, [productCodes]);

  const productOptions = productCodes.map((p) => ({
    value: p,
    label: p,
  }));

  // Group reviews by month & product
  const monthlyAverages = useMemo(() => {
    const data = {};

    reviews.forEach((r) => {
      const date = new Date(r[3]);
      if (isNaN(date)) return;

      const month = date.toLocaleString("default", { month: "short" }); // "Jan", "Feb"
      const rating = parseFloat(r[4]);
      const product = r[6] ? String(r[6]).trim() : "Unknown";

      if (!rating) return;

      if (!data[month]) data[month] = { All: [], products: {} };

      data[month].All.push(rating);

      if (!data[month].products[product]) data[month].products[product] = [];
      data[month].products[product].push(rating);
    });

    const months = Object.keys(data).sort(
      (a, b) => new Date(`${a} 1, 2023`) - new Date(`${b} 1, 2023`)
    );

    const result = { months, averages: { All: [] } };

    months.forEach((month) => {
      const monthData = data[month];
      result.averages.All.push(
        monthData.All.reduce((a, b) => a + b, 0) / monthData.All.length
      );

      Object.keys(monthData.products).forEach((prod) => {
        if (!result.averages[prod]) result.averages[prod] = [];
        result.averages[prod].push(
          monthData.products[prod].reduce((a, b) => a + b, 0) /
            monthData.products[prod].length
        );
      });
    });

    return result;
  }, [reviews]);

  useEffect(() => {
  if (!monthlyAverages.months.length) return;
  const ctx = document.getElementById(chartId).getContext("2d");

  if (chartRef.current) chartRef.current.destroy();

  const datasets = [];

  // ✅ Show "All Products" only if nothing is selected
  if (selectedProducts.length === 0) {
    datasets.push({
      label: "All Products",
      data: monthlyAverages.averages.All,
      borderColor: "rgba(75,192,192,1)",
      backgroundColor: "rgba(75,192,192,0.2)",
      tension: 0.3,
    });
  } else {
    selectedProducts.forEach((prod) => {
      if (prod === "All") return;
      datasets.push({
        label: prod,
        data: monthlyAverages.averages[prod] || [],
        borderColor: productColors[prod],
        backgroundColor: productColors[prod],
        tension: 0.3,
      });
    });
  }

  // ✅ Find lowest average across the displayed datasets
  const allValues = datasets.flatMap((d) => d.data).filter((v) => v > 0);
  let yMin = 4;
  if (allValues.length) {
    const minVal = Math.min(...allValues);
    if (minVal < 4) {
      yMin = Math.floor(minVal); // shift to nearest lower integer
    }
  }

  chartRef.current = new ChartJS(ctx, {
    type: "line",
    data: {
      labels: monthlyAverages.months,
      datasets,
    },
    options: {
      responsive: true,
      plugins: {
        title: { display: true, text: "Average Rating by Month" },
        legend: {
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 7,
            boxHeight: 7,
          },
        },
      },
      scales: {
        y: {
          min: yMin,
          max: 5.1,
          ticks: {
            stepSize: 0.2,
            callback: function (value) {
              return value > 5 ? null : value;
            },
          },
        },
      },
    },
  });

  return () => {
    if (chartRef.current) chartRef.current.destroy();
  };
}, [monthlyAverages, selectedProducts, productColors, chartId]);


  // Handle checkbox selection
  // const toggleProduct = (code) => {
  //   setSelectedProducts((prev) =>
  //     prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
  //   );
  // };

  return (
    <div>
      <div>
        <label className="comparison-label">
          <b>Product Comparison Graph</b>
        </label>
        <div
          style={{
            border: "1px solid #ccc",
            padding: "6px",
            marginBottom: "20px",
            marginTop: "20px",
            borderRadius: "6px",
          }}
        >
          <Select
            isMulti
            placeholder="Select products..."
            options={productOptions}
            onChange={(selected) => {
              setSelectedProducts(selected ? selected.map((s) => s.value) : []);
            }}
          />
        </div>
      </div>

      <canvas id={chartId}></canvas>
    </div>
  );
};

export default ComparisonGraph;
